import { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import AppSideBar from '../../../ui-components/AppSideBar';

import {
  useLazyGetPermissionListQuery,
  useLazyGetRoleByIdQuery,
  useUpdateRoleCapabilitiesMutation,
} from './roleApi';

import type { UpdateRoleCapabilitiesRequest, PermissionRecord } from './model';

const RoleData = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedGroupCode, setSelectedGroupCode] = useState<string | null>(
    null
  );
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [permissionRecords, setPermissionRecords] = useState<
    PermissionRecord[]
  >([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [triggerGetPermissions, { data: permissionsData }] =
    useLazyGetPermissionListQuery();
  const [triggerGetRole, { data: roleData }] = useLazyGetRoleByIdQuery();
  const [updateRoleCapabilities] = useUpdateRoleCapabilitiesMutation();
  const defaultGroupId = id ? parseInt(id, 10) : undefined;

  useEffect(() => {
    if (defaultGroupId === undefined) {
      console.error('No groupId provided in URL!');
      return;
    }

    const fetchInitialData = async () => {
      try {
        await triggerGetPermissions().unwrap();
        await triggerGetRole(defaultGroupId).unwrap();
      } catch (err) {
        toast.error(
          (err as any)?.settings?.message ||
            (err as any)?.message ||
            'An error occurred'
        );
      }
    };
    fetchInitialData();
  }, [defaultGroupId, triggerGetPermissions, triggerGetRole]);

  useEffect(() => {
    if (roleData?.data?.capabilities) {
      const caps = roleData.data.capabilities.map((c: string) =>
        c.toLowerCase()
      );
      setCapabilities(caps);

      if (!hasUserSelected && roleData.data.groupCode) {
        setSelectedGroupCode(roleData.data.groupCode);
      }
    }
  }, [hasUserSelected, roleData]);

  useEffect(() => {
    if (permissionsData?.data?.records) {
      setPermissionRecords(permissionsData.data.records);
    }
  }, [permissionsData]);

  const handleGroupClick = (groupCode: string) => {
    setHasUserSelected(true);
    setSelectedGroupCode(groupCode);
  };

  const handleCheckboxToggle = (code: string) => {
    const normalizedCode = code.toLowerCase();
    setCapabilities((prev) =>
      prev.includes(normalizedCode)
        ? prev.filter((c) => c !== normalizedCode)
        : [...prev, normalizedCode]
    );
  };

  const filteredRecords = permissionRecords.filter((group) =>
    group?.group?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedPermissionRecords = filteredRecords
    .map((group) => ({
      ...group,
      checkedCount: group.permissions.filter((perm: any) =>
        capabilities.includes(perm.permissionCode.toLowerCase())
      ).length,
    }))
    .sort((a, b) => {
      if (b.checkedCount !== a.checkedCount) {
        return b.checkedCount - a.checkedCount;
      }
      return a.group.localeCompare(b.group);
    });

  useEffect(() => {
    if (!hasUserSelected && sortedPermissionRecords.length > 0) {
      setSelectedGroupCode(sortedPermissionRecords[0].groupCode);
    }
  }, [sortedPermissionRecords, hasUserSelected]);

  const selectedGroup = permissionRecords?.find(
    (g: PermissionRecord) => g.groupCode === selectedGroupCode
  );

  const handleUpdate = async () => {
    if (!defaultGroupId || !roleData?.data) {
      return;
    }

    const payload: UpdateRoleCapabilitiesRequest = {
      groupId: defaultGroupId,
      groupCode: roleData?.data?.groupCode ?? '',
      groupName: roleData?.data?.groupName ?? '',
      capabilities,
    };

    try {
      const response = await updateRoleCapabilities(payload).unwrap();
      toast.success(response.settings.message);
      navigate('/roles');
    } catch (err) {
      toast.error(
        (err as any)?.settings?.message ||
          (err as any)?.message ||
          'Update failed'
      );
    }
  };

  if (!permissionRecords.length || !roleData) {
    return (
      <div className="p-6 text-gray-600 dark:text-gray-300">
        Loading permissions...
      </div>
    );
  }

  return (
    <div className=" bg-[#f9fafb] dark:bg-gray-800 flex flex-col card gap-6 h-[calc(100vh-9rem)]">
      <div className="flex flex-wrap justify-between items-center">
        <h1 className="top-title">Update Role: {roleData?.data?.groupName}</h1>

        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
          >
            Back to List
          </button>
          <button
            type="button"
            onClick={handleUpdate}
            className="px-8 py-2.5 rounded-lg text-sm font-bold bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40 transition-all uppercase tracking-wide"
          >
            Update
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 overflow-y-auto">
        <div className="w-full sm:w-1/3 lg:w-1/4">
          <AppSideBar
            groupTypeArr={sortedPermissionRecords}
            selectedGroup={selectedGroupCode}
            onSelect={handleGroupClick}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showSearch={true}
          />
        </div>

        <div className="w-full sm:w-2/3 lg:w-7/4">
          {selectedGroup ? (
            <div className="bg-white h-full dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow p-6 rounded-md">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedGroup?.permissions?.map((perm) => {
                  const normalizedCode = perm?.permissionCode.toLowerCase();
                  const isChecked = capabilities.includes(normalizedCode);

                  return (
                    <div
                      key={perm?.permissionId}
                      className="flex items-start gap-3 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg hover:shadow-sm transition"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() =>
                          handleCheckboxToggle(perm?.permissionCode)
                        }
                        className="mt-1 h-4 w-4 rounded border-neutral-300 dark:border-neutral-700 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                      <div>
                        <label className="text-sm text-brand-700 dark:text-brand-300">
                          {perm?.permissionName}
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              Select a group to view permissions
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleData;
